import { memo, useEffect, useMemo, useState } from 'react';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import RedoOutlined from '@ant-design/icons/RedoOutlined';
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';
import StopOutlined from '@ant-design/icons/StopOutlined';
import SyncOutlined from '@ant-design/icons/SyncOutlined';
import { useTranslation } from 'react-i18next';
import type { RunRefusal } from '@/api/runs';
import type { DocumentLink } from '@/utils/messageParser';
import AnswerBody from '../answer/AnswerBody';
import { comparisonOutcome } from '../answer/comparisonOutcome';
import { requestOpenSource } from '../answer/sourceEvents';
import { useRun } from '../runs/runStore';
import type { ConvMessage } from '../types';
import {
  answerActions,
  answerView,
  type AnswerStatus,
  type AnswerView,
} from './answerState';
import ModeBadge from './ModeBadge';
import RunStatusLine from './RunStatusLine';
import SourceList from './SourceList';
import styles from './conversation.module.css';

export interface AnswerItemProps {
  message: ConvMessage;
  /** Its answer number; null for a question the server removed. */
  number: number | null;
  /** The number of the answer it retries or offers an alternative to. */
  parentNumber: number | null;
  /** A run is active (or being sent) in this Q&A: resubmissions wait. */
  busy: boolean;
  links: DocumentLink[];
  /** Why the last Retry or Create another answer of it was refused. */
  refusal: RunRefusal | null;
  /**
   * Said on Retry and Create another answer when the answer has no recorded
   * mode: they are then sent in the mode chosen now.
   */
  unrecordedModeNote: string;
  onStop: (runId: string) => void;
  onRetry: (message: ConvMessage) => void;
  onAlternative: (message: ConvMessage) => void;
  onReconnect: (runId: string) => void;
  onEdit: (message: ConvMessage) => void;
  /**
   * Copy the answer, or one of its tables, with formatting; resolves to
   * whether the clipboard took it (else the manual-copy dialog opens).
   */
  onCopy: (
    message: ConvMessage,
    view: AnswerView,
    table: number | null,
  ) => Promise<boolean>;
  /** CSV export of this answer (the dialog chooses the scope). */
  onExport: (message: ConvMessage, comparisonTable: boolean) => void;
}

/** A source's exclusion reason in words where the dictionary has them. */
const REASON_KEYS: Record<string, string> = {
  deleted: 'v2.composer.ex_removed',
  pending: 'v2.composer.ex_processing',
  processing: 'v2.composer.ex_processing',
  image_only: 'v2.composer.ex_failed',
  no_text: 'v2.composer.ex_failed',
  processing_failed: 'v2.composer.ex_failed',
};

/** A Stop is on its way or recorded; after a failed cancel it can be retried. */
const stopPending = (status: AnswerStatus) =>
  'stop' in status && status.stop !== 'none' && status.stop !== 'failed';

const Refusal = ({
  refusal,
  onEdit,
}: {
  refusal: RunRefusal;
  onEdit: () => void;
}) => {
  const { t } = useTranslation();
  const unavailable = refusal.code === 'sources_unavailable';
  return (
    <div className={styles.refusal} role="alert" data-testid="answer-refusal">
      {unavailable && refusal.reason === 'not_recorded' ? (
        <p>{t('v2.messages.sourcesNotRecorded')}</p>
      ) : unavailable ? (
        <>
          <p>{t('v2.messages.sourcesGone')}</p>
          <ul>
            {(refusal.sources ?? []).map(source => (
              <li key={source.id}>
                {source.name ?? t('v2.common.notRecorded')}
                {t('v2.common.paren', {
                  text: REASON_KEYS[source.reason]
                    ? t(REASON_KEYS[source.reason])
                    : source.reason,
                })}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>{refusal.message}</p>
      )}
      <p>{t('v2.messages.useEdit')}</p>
      <button type="button" className={styles.action} onClick={onEdit}>
        {t('v2.messages.editResubmit')}
      </button>
    </div>
  );
};

/** One answer: mode, status, body, sources and its actions (spec §3, §5). */
const AnswerItem = ({
  message,
  number,
  parentNumber,
  busy,
  links,
  refusal,
  unrecordedModeNote,
  onStop,
  onRetry,
  onAlternative,
  onReconnect,
  onEdit,
  onCopy,
  onExport,
}: AnswerItemProps) => {
  const { t } = useTranslation();
  const run = useRun(message.run_id);
  const view = answerView(message, run);
  const actions = answerActions(message, view);
  const runId = message.run_id ?? null;
  const mode = message.mode ?? run?.mode ?? null;
  const comparison = message.comparison;
  const [copied, setCopied] = useState(false);
  const completed = view.status.kind === 'completed';
  // Only a complete comparison can say there are no differences.
  const outcome = useMemo(
    () =>
      mode === 'comparison' && completed && view.text && !view.legacy
        ? comparisonOutcome(view.text)
        : null,
    [completed, mode, view.legacy, view.text],
  );

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = (table: number | null) => {
    void onCopy(message, view, table).then(ok => {
      if (ok) setCopied(true);
    });
  };

  const kindLabel =
    message.kind === 'retry'
      ? t('v2.selection.retryOf', { n: parentNumber ?? '—' })
      : message.kind === 'alternative'
        ? t('v2.messages.regenOf', { n: parentNumber ?? '—' })
        : null;

  return (
    <article
      className={styles.answerItem}
      aria-label={
        number != null ? t('v2.messages.answerN', { n: number }) : undefined
      }
      data-testid="chat-bubble-answer"
      data-message-id={message.id}
      data-run-id={runId ?? undefined}
    >
      <div className={styles.answerHead}>
        <ModeBadge mode={mode} />
        {kindLabel ? (
          <span className={styles.caption} data-testid="answer-kind">
            {kindLabel}
          </span>
        ) : null}
      </div>
      {comparison ? (
        <p className={styles.caption} data-testid="answer-comparison">
          {comparison.purpose === 'guidelines'
            ? [
                t('v2.composer.cmpRefCheck'),
                t('v2.common.labelValue', {
                  label: t('v2.comparison.cmpDocToCheck'),
                  value:
                    comparison.document_to_check?.name ??
                    t('v2.common.notRecorded'),
                }),
              ].join(' · ')
            : [
                t('v2.composer.cmpVersion'),
                t('v2.common.labelValue', {
                  label: t('v2.composer.cmpBaseline'),
                  value:
                    comparison.baseline?.name ?? t('v2.common.notRecorded'),
                }),
              ].join(' · ')}
        </p>
      ) : null}
      <RunStatusLine status={view.status} />
      {view.text ? (
        <AnswerBody
          text={view.text}
          streaming={view.streaming}
          partial={view.partial}
          legacy={view.legacy}
          citations={view.citations}
          messageId={message.id}
          runId={runId}
          links={links}
          onCopyTable={index => copy(index)}
          comparison={mode === 'comparison'}
        />
      ) : null}
      {outcome && outcome !== 'differences' ? (
        <p
          className={
            outcome === 'no_differences' ? styles.outcomeOk : styles.outcomeWarn
          }
          role="status"
          data-testid="comparison-outcome"
          data-outcome={outcome}
        >
          {outcome === 'no_differences'
            ? t('v2.comparison.cmpNoDiff')
            : outcome === 'no_table'
              ? t('v2.comparison.cmpNoTable')
              : t('v2.comparison.cmpIncomplete')}
        </p>
      ) : null}
      {view.partialLabel ? (
        <span className={styles.partial} data-testid="answer-partial">
          {t('v2.messages.partialNote')}
        </span>
      ) : null}
      {comparison?.purpose === 'guidelines' && view.text ? (
        <p className={styles.caption}>{t('v2.comparison.cmpDisclaimer')}</p>
      ) : null}
      {view.citations?.length && view.text ? (
        <SourceList
          citations={view.citations}
          onOpen={(citation, opener) =>
            requestOpenSource({
              messageId: message.id,
              runId,
              n: citation.n,
              citation,
              opener,
            })
          }
        />
      ) : null}
      {refusal ? (
        <Refusal refusal={refusal} onEdit={() => onEdit(message)} />
      ) : null}
      <div className={styles.actions}>
        {actions.stop && runId ? (
          <button
            type="button"
            className={styles.action}
            data-testid="answer-stop"
            disabled={stopPending(view.status)}
            onClick={() => onStop(runId)}
          >
            <StopOutlined aria-hidden />
            {t('v2.common.stop')}
          </button>
        ) : null}
        {!actions.stop && actions.hasBody ? (
          <>
            <button
              type="button"
              className={styles.iconAction}
              aria-label={
                copied ? t('v2.messages.copied') : t('v2.messages.copyAnswer')
              }
              title={
                copied ? t('v2.messages.copied') : t('v2.messages.copyAnswer')
              }
              onClick={() => copy(null)}
              data-testid="answer-copy"
            >
              {copied ? (
                <CheckOutlined aria-hidden />
              ) : (
                <CopyOutlined aria-hidden />
              )}
            </button>
            {message.local ? null : (
              <button
                type="button"
                className={styles.iconAction}
                aria-label={t('v2.common.exportCsv')}
                title={t('v2.common.exportCsv')}
                onClick={() =>
                  onExport(
                    message,
                    mode === 'comparison' &&
                      completed &&
                      outcome !== null &&
                      outcome !== 'no_table',
                  )
                }
                data-testid="answer-csv"
              >
                <DownloadOutlined aria-hidden />
              </button>
            )}
            {copied ? (
              <span role="status" className={styles.copied}>
                {t('v2.messages.copied')}
              </span>
            ) : null}
          </>
        ) : null}
        {actions.reconnect && runId ? (
          <button
            type="button"
            className={styles.action}
            data-testid="answer-reconnect"
            onClick={() => onReconnect(runId)}
          >
            <SyncOutlined aria-hidden />
            {t('v2.messages.reconnect')}
          </button>
        ) : null}
        {actions.retry ? (
          <button
            type="button"
            className={styles.action}
            data-testid="answer-retry"
            title={message.mode ? undefined : unrecordedModeNote}
            disabled={busy}
            onClick={() => onRetry(message)}
          >
            <ReloadOutlined aria-hidden />
            {t('v2.common.retry')}
          </button>
        ) : null}
        {actions.alternative ? (
          <button
            type="button"
            className={styles.action}
            data-testid="answer-alternative"
            title={message.mode ? undefined : unrecordedModeNote}
            disabled={busy}
            onClick={() => onAlternative(message)}
          >
            <RedoOutlined aria-hidden />
            {t('v2.messages.regenerate')}
          </button>
        ) : null}
      </div>
      {(actions.retry || actions.alternative) && !message.mode ? (
        <p className={styles.caption} data-testid="answer-mode-note">
          {unrecordedModeNote}
        </p>
      ) : null}
    </article>
  );
};

export default memo(AnswerItem);
